import React, { useState, useRef, useEffect } from 'react';
import { Moon, Palette, Sun, ChevronDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from './Button';

interface ThemeOption {
  value: 'light' | 'dark' | 'darker';
  label: string;
  description: string;
  icon: React.ReactNode;
}

const themeOptions: ThemeOption[] = [
  {
    value: 'light',
    label: 'Light Mode',
    description: 'Optimized for bright environments',
    icon: <Sun className="w-4 h-4" />
  },
  {
    value: 'dark',
    label: 'Dark Mode',
    description: 'Deep blue-gray theme for low-light environments',
    icon: <Moon className="w-4 h-4" />
  },
  {
    value: 'darker',
    label: 'Ultra Dark Mode',
    description: 'Off-black theme with gold accents',
    icon: <Palette className="w-4 h-4" />
  }
];

export const ThemeDropdown: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentTheme = themeOptions.find(option => option.value === theme) || themeOptions[1];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleThemeSelect = (selectedTheme: 'light' | 'dark' | 'darker') => {
    setTheme(selectedTheme);
    setIsOpen(false);
  };

  return (
    <div className="relative z-[9999]" ref={dropdownRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 md:gap-2 justify-between min-w-0"
        title="Select theme"
      >
        <div className="flex items-center gap-1 md:gap-2 min-w-0">
          {currentTheme.icon}
          <span className="hidden sm:inline truncate">{currentTheme.label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 theme-bg-card border theme-border rounded-lg shadow-lg z-[9999]">
          <div className="p-2 space-y-1">
            {themeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleThemeSelect(option.value)}
                className={`w-full p-3 rounded-lg text-left transition-all duration-200 flex items-start gap-3 ${
                  theme === option.value
                    ? 'theme-bg-sidebar-hover border theme-border theme-text-primary'
                    : 'theme-text-secondary hover:theme-bg-sidebar-hover hover:theme-text-primary'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs theme-text-muted mt-0.5">{option.description}</div>
                </div>
                {theme === option.value && (
                  <div className="flex-shrink-0 w-2 h-2 theme-accent-yellow rounded-full mt-1.5" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
